/**
 * Passphrase-to-32-byte-secret derivation.
 *
 * Used by the dashboard's first-run wizard to turn a user-typed
 * passphrase into the 32-byte AES-GCM key that wraps `O_sk`. We use
 * `scrypt` from `@noble/hashes` — memory-hard, much harder to brute-
 * force than PBKDF2, available everywhere the dashboard runs (no
 * WebCrypto Argon2 yet).
 *
 * Parameters: N=2^15 (32768), r=8, p=1 → ~250–500 ms on a typical
 * laptop. Each enrollment uses a fresh 16-byte salt persisted alongside
 * the wrapped blob (the server stores both transparently).
 *
 * Wire format for the wrapped+passphrase-derivable bundle:
 *
 *   wrappedKeypairBlob = salt(16) ‖ nonce(12) ‖ aes_gcm_ciphertext
 *
 * The 16-byte salt prefix is what `wrapSecretKey` does NOT add —
 * passphrase callers prepend it via `derivePassphraseSecretWithSalt`.
 */

import { scrypt } from "@noble/hashes/scrypt";

const SCRYPT_N = 1 << 15; // 32768
const SCRYPT_R = 8;
const SCRYPT_P = 1;
const SECRET_BYTES = 32;
const SALT_BYTES = 16;

/** Derive a 32-byte secret from (passphrase, salt). Both inputs are
 * arbitrary bytes; pass UTF-8 of the user's typed passphrase. */
export async function derivePassphraseSecret(
  passphraseUtf8: Uint8Array,
  salt: Uint8Array,
): Promise<Uint8Array> {
  if (salt.length !== SALT_BYTES) {
    throw new Error(`salt must be ${SALT_BYTES} bytes, got ${salt.length}`);
  }
  // scrypt is sync but heavy; in production we'd run inside a Worker.
  // Acceptable in L1 because enrollment + login are infrequent gestures
  // explicitly initiated by the user.
  const out = scrypt(passphraseUtf8, salt, {
    N: SCRYPT_N,
    r: SCRYPT_R,
    p: SCRYPT_P,
    dkLen: SECRET_BYTES,
  });
  return out;
}

/** Generate a fresh 16-byte random salt for a new enrollment. */
export function freshSalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(SALT_BYTES));
}

/**
 * Convenience wrapper: prepend the salt to a wrapped-blob so the
 * passphrase path is self-describing on the wire. Pairs with
 * `splitSaltAndWrapped` on the unwrap side.
 */
export function prependSalt(salt: Uint8Array, wrappedBlob: Uint8Array): Uint8Array {
  if (salt.length !== SALT_BYTES) {
    throw new Error(`salt must be ${SALT_BYTES} bytes, got ${salt.length}`);
  }
  const out = new Uint8Array(SALT_BYTES + wrappedBlob.length);
  out.set(salt, 0);
  out.set(wrappedBlob, SALT_BYTES);
  return out;
}

export function splitSaltAndWrapped(
  bundle: Uint8Array,
): { salt: Uint8Array; wrappedBlob: Uint8Array } {
  if (bundle.length < SALT_BYTES + 12 + 16) {
    throw new Error(`bundle too short: ${bundle.length}`);
  }
  return {
    salt: bundle.slice(0, SALT_BYTES),
    wrappedBlob: bundle.slice(SALT_BYTES),
  };
}

/** Encode a passphrase string as UTF-8 bytes. */
export function encodePassphrase(s: string): Uint8Array {
  return new TextEncoder().encode(s.normalize("NFC"));
}
